/**
 * Export: hand over the file, and say plainly what it is not.
 *
 * The MusicXML comes back from the generator already engraved — each part in its own key and
 * clef. The panel below the table is deliberate: a reader who takes this for a finished piece
 * has been misled, so the page says what was left out before they open it in a notation app.
 */

import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Board from "../components/Board";
import { ACTION, ACTION_LIVE } from "../components/Chrome";
import { Kicker } from "../components/Page";
import { bytes, rate } from "../lib/format";
import { useBrief } from "../state/brief";

const CLEFS: Record<string, string> = {
  G: "treble",
  F: "bass",
  C: "alto",
  percussion: "percussion",
};

export default function ExportPage(): JSX.Element {
  const brief = useBrief();
  const result = brief.result;
  const [copied, setCopied] = useState(false);
  if (result === null) return <Navigate to="/write" replace />;

  const xml = result.musicxml ?? "";
  const size = new TextEncoder().encode(xml).length;

  const save = (): void => {
    const blob = new Blob([xml], { type: "application/vnd.recordare.musicxml+xml" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = result.fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copy = (): void => {
    void navigator.clipboard.writeText(brief.chords.join(" | ")).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Board
      kicker="Export"
      title={result.fileName}
      back={{ to: "/score", label: "The result" }}
      lede={`${result.total} chord${result.total === 1 ? "" : "s"} · verdict ${rate(result.verdict?.score)}`}
      actions={
        <>
          <button type="button" onClick={copy} className={ACTION}>
            {copied ? "Copied" : "Copy the chords"}
          </button>
          <button type="button" onClick={save} disabled={xml.length === 0} className={ACTION_LIVE}>
            Save MusicXML
          </button>
        </>
      }
    >
      <div className="space-y-8">
        <div className="rounded-xl border border-rule bg-leaf p-4">
          <div className="font-mono text-sm">{result.fileName}</div>
          <p className="mt-1 text-xs text-ink-faded">
            MusicXML 4.0, score-partwise · {result.staves.length} parts · {result.total} bars ·{" "}
            {bytes(size)}
          </p>
        </div>

        <section className="space-y-2">
          <Kicker>Parts as engraved</Kicker>
          <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-quiet">
                  <th className="px-4 py-2 font-medium">Part</th>
                  <th className="px-2 py-2 font-medium">Clef</th>
                  <th className="px-4 py-2 text-right font-medium">Transpose</th>
                </tr>
              </thead>
              <tbody>
                {result.staves.map((staff) => (
                  <tr key={staff.partId} className="border-t border-rule-faint">
                    <td className="px-4 py-2.5">{staff.name}</td>
                    <td className="px-2 py-2.5 font-mono text-ink-faded">
                      {CLEFS[staff.clef.sign] ?? staff.clef.sign}
                      {staff.clef.octaveChange
                        ? ` ${staff.clef.octaveChange > 0 ? "8va" : "8vb"}`
                        : ""}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-ink-quiet">
                      {staff.transpose === 0
                        ? "at pitch"
                        : `${staff.transpose > 0 ? "+" : ""}${staff.transpose}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs leading-relaxed text-ink-faded">
            Each part is written in the instrument&apos;s own key and clef — the way its table row
            is read, not the way the chord sounds.
          </p>
        </section>

        <section className="rounded-xl border border-rule bg-leaf p-4">
          <Kicker>What the file is not</Kicker>
          <p className="mt-2 text-sm leading-relaxed text-ink-faded">
            One whole note per chord, no tempo, no phrasing, and no dynamics beyond the one mark
            each seat was given. It is a voicing and a seating plan — the music is still yours to
            write.
          </p>
        </section>

        <Link
          to="/write"
          className="inline-flex h-12 items-center rounded-[10px] border border-[#C6BFAE] px-5 text-[15px] font-medium"
        >
          Write another
        </Link>
      </div>
    </Board>
  );
}
